<template>
  <div v-if="visible" class="keep-in-touch">
    <h3 class="keep-in-touch__title">
      {{ t('loginRegister.keepInTouch.title') }}
    </h3>

    <div class="keep-in-touch__options">
      <Checkbox
        id="optin-email-deals"
        :active="consent.email"
        @checkboxClick="toggleEmailDeals"
      >
        <div class="keep-in-touch__option-text">
          <span class="keep-in-touch__label">{{
            t('loginRegister.keepInTouch.emailDealsLabel')
          }}</span>
          <span class="keep-in-touch__desc">{{
            t('loginRegister.keepInTouch.emailDealsDesc')
          }}</span>
        </div>
      </Checkbox>

      <Checkbox
        id="optin-partenaires"
        :active="consent.partenaires"
        @checkboxClick="togglePartenaires"
      >
        <div class="keep-in-touch__option-text">
          <span class="keep-in-touch__label">{{
            t('loginRegister.keepInTouch.partenairesLabel')
          }}</span>
          <span class="keep-in-touch__desc" v-html="t('loginRegister.keepInTouch.partenairesDesc')" @click="stopIfLinkClick" />
        </div>
      </Checkbox>
    </div>

    <button
      type="button"
      class="keep-in-touch__personalize"
      @click="openPersonalize"
    >
      {{ t('loginRegister.keepInTouch.personalize') }}
    </button>

    <teleport to="body">
      <div
        v-if="showPersonalize"
        class="prefs-modal-backdrop"
        @click.self="closePersonalize(false)"
      >
        <div class="prefs-modal" role="dialog" aria-modal="true">
          <div class="prefs-modal__header">
            <h3 class="prefs-modal__title">
              {{ t('loginRegister.keepInTouch.personalizeTitle') }}
            </h3>
            <button
              type="button"
              class="prefs-modal__close"
              aria-label="Close"
              @click="closePersonalize(false)"
            >
              &times;
            </button>
          </div>

          <div class="prefs-modal__body">
            <section class="prefs-section">
              <h4 class="prefs-section__title">
                {{ t('loginRegister.keepInTouch.sectionDealsTitle') }}
              </h4>
              <p class="prefs-section__intro">
                {{ t('loginRegister.keepInTouch.sectionDealsIntro') }}
              </p>

              <div class="prefs-row">
                <div class="prefs-row__text">
                  <span class="prefs-row__label">{{
                    t('loginRegister.keepInTouch.switchEmail')
                  }}</span>
                  <span class="prefs-row__desc">{{
                    t('loginRegister.keepInTouch.switchEmailDesc')
                  }}</span>
                </div>
                <ToggleSwitch
                  :active="draft.email"
                  :label="t('loginRegister.keepInTouch.switchEmail')"
                  @toggle="draft.email = !draft.email"
                />
              </div>

              <div class="prefs-row">
                <div class="prefs-row__text">
                  <span class="prefs-row__label">{{
                    t('loginRegister.keepInTouch.switchSms')
                  }}</span>
                  <span class="prefs-row__desc">{{
                    t('loginRegister.keepInTouch.switchSmsDesc')
                  }}</span>
                </div>
                <ToggleSwitch
                  :active="draft.sms"
                  :label="t('loginRegister.keepInTouch.switchSms')"
                  @toggle="draft.sms = !draft.sms"
                />
              </div>
            </section>

            <hr class="prefs-separator" />

            <section class="prefs-section">
              <h4 class="prefs-section__title">
                {{ t('loginRegister.keepInTouch.sectionPartnersTitle') }}
              </h4>
              <p
                class="prefs-section__intro"
                v-html="t('loginRegister.keepInTouch.sectionPartnersIntro')"
                @click="stopIfLinkClick"
              />

              <div class="prefs-row prefs-row--start">
                <div class="prefs-row__text">
                  <span class="prefs-row__label">{{
                    t('loginRegister.keepInTouch.switchPartenaires')
                  }}</span>
                  <span
                    class="prefs-row__desc"
                    v-html="t('loginRegister.keepInTouch.switchPartenairesDesc')"
                    @click="stopIfLinkClick"
                  />
                </div>
                <ToggleSwitch
                  :active="draft.partenaires"
                  :label="t('loginRegister.keepInTouch.switchPartenaires')"
                  @toggle="draft.partenaires = !draft.partenaires"
                />
              </div>
            </section>

            <hr class="prefs-separator" />

            <section class="prefs-section">
              <h4 class="prefs-section__title">
                {{ t('loginRegister.keepInTouch.sectionTrackingTitle') }}
              </h4>
              <p class="prefs-section__intro">
                {{ t('loginRegister.keepInTouch.sectionTrackingIntro') }}
              </p>

              <div class="prefs-row prefs-row--start">
                <div class="prefs-row__text">
                  <span class="prefs-row__label">{{
                    t('loginRegister.keepInTouch.switchTracking')
                  }}</span>
                  <span class="prefs-row__desc">{{
                    t('loginRegister.keepInTouch.switchTrackingDesc')
                  }}</span>
                </div>
                <ToggleSwitch
                  :active="draft.trackingOpen"
                  :label="t('loginRegister.keepInTouch.switchTracking')"
                  @toggle="draft.trackingOpen = !draft.trackingOpen"
                />
              </div>
            </section>
          </div>

          <div class="prefs-modal__footer">
            <Button
              class="w-100"
              @buttonClick="savePersonalize"
            >
              <template #text>
                <span>{{ t('loginRegister.keepInTouch.save') }}</span>
              </template>
            </Button>
            <button
              type="button"
              class="prefs-modal__back"
              @click="closePersonalize(false)"
            >
              {{ t('loginRegister.keepInTouch.back') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script lang="ts" setup>
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import Checkbox from '@/components/LoginRegister/common/inputs/Checkbox/v1/Checkbox.vue'
  import Button from '@/components/LoginRegister/common/Button/v1/Button.vue'
  import ToggleSwitch from '@/components/common/ToggleSwitch/v1/ToggleSwitch.vue'
  import isSubscribeNewsletter from '@/state/isSubscribeNewsletter'
  import registerForm from '@/state/registerForm'
  import { CdaConsent, defaultCdaConsent } from '@/types/CdaConsent'
  import { saveCdaConsent } from '@/logic/contact/cdaConsent'

  const { t } = useI18n()
  const draft = reactive<CdaConsent>(defaultCdaConsent())
  const showForThisVisit = ref(true)
  const showPersonalize = ref(false)

  const stopIfLinkClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null
    if (target?.closest?.('a')) {
      event.stopPropagation()
    }
  }

  onMounted(() => {
    showForThisVisit.value = !isSubscribeNewsletter.value.email
  })

  const consent = computed(() => isSubscribeNewsletter.value)
  const visible = computed(() => showForThisVisit.value)

  const formEmail = computed(() => registerForm.value?.email?.trim() || '')

  /** Local state always; soft CRM push only when email already known (parity with funnel). */
  const commitConsent = async (next: CdaConsent) => {
    isSubscribeNewsletter.value = { ...next, whatsapp: false }
    if (formEmail.value) {
      await saveCdaConsent(formEmail.value, isSubscribeNewsletter.value)
    }
  }

  const toggleEmailDeals = async () => {
    const enabled = !consent.value.email
    await commitConsent({
      ...consent.value,
      email: enabled,
      trackingOpen: enabled,
    })
  }

  const togglePartenaires = async () => {
    await commitConsent({
      ...consent.value,
      partenaires: !consent.value.partenaires,
    })
  }

  const syncDraftFromConsent = () => {
    Object.assign(draft, { ...consent.value, whatsapp: false })
  }

  const openPersonalize = () => {
    syncDraftFromConsent()
    showPersonalize.value = true
  }

  const closePersonalize = (saved: boolean) => {
    if (!saved) syncDraftFromConsent()
    showPersonalize.value = false
  }

  const savePersonalize = async () => {
    await commitConsent({ ...draft, whatsapp: false })
    closePersonalize(true)
  }
</script>

<style scoped lang="scss">
  .keep-in-touch {
    margin: 1.25rem 0 1.5rem;
    text-align: left;
  }

  .keep-in-touch__title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #0d141b;
    margin: 0 0 0.85rem;
  }

  .keep-in-touch__options {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .keep-in-touch__options :deep(.d-flex) {
    align-items: flex-start;
  }

  .keep-in-touch__options :deep(input[type='checkbox']) {
    margin-top: 0.2rem;
    flex-shrink: 0;
  }

  .keep-in-touch__option-text {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    text-align: left;
  }

  .keep-in-touch__label {
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1.3;
    color: #0d141b;
  }

  .keep-in-touch__desc {
    font-size: 0.8rem;
    color: #585f66;
    line-height: 1.35;

    :deep(a) {
      color: inherit;
      font-weight: 700;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .keep-in-touch__personalize {
    display: inline-block;
    margin-top: 0.85rem;
    padding: 0;
    border: 0;
    background: none;
    color: var(--primary-blue, #0042b8);
    font-size: 0.9rem;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
  }

  .prefs-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: rgba(13, 20, 27, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  .prefs-modal {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    max-height: min(90vh, 900px);
    width: min(801px, 100%);
    display: flex;
    flex-direction: column;
  }

  .prefs-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem 1rem;
    border-bottom: 1px solid #e9ecef;
  }

  .prefs-modal__title {
    margin: 0;
    font-size: 1.375rem;
    font-weight: 800;
    color: #0d141b;
  }

  .prefs-modal__close {
    width: 32px;
    height: 32px;
    border: 0;
    background: none;
    font-size: 1.75rem;
    line-height: 1;
    color: #1d2329;
    cursor: pointer;
  }

  .prefs-modal__body {
    padding: 1.25rem 1.5rem;
    overflow-y: auto;
  }

  .prefs-section {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .prefs-section__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 800;
    color: #0d141b;
  }

  .prefs-section__intro {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.35;
    color: #585f66;

    :deep(a) {
      color: inherit;
      font-weight: 700;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .prefs-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    &--start {
      align-items: flex-start;
    }
  }

  .prefs-row__text {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    flex: 1;
  }

  .prefs-row__label {
    font-size: 1rem;
    font-weight: 700;
    color: #0d141b;
  }

  .prefs-row__desc {
    font-size: 0.875rem;
    color: #585f66;

    :deep(a) {
      color: inherit;
      font-weight: 700;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .prefs-separator {
    margin: 1.25rem 0;
    border: 0;
    border-top: 1px solid #dee2e6;
  }

  .prefs-modal__footer {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 1.5rem 1.5rem;
    box-shadow: 0 -1px 4px rgba(16, 24, 40, 0.08);
  }

  .prefs-modal__back {
    padding: 0;
    border: 0;
    background: none;
    color: var(--primary-blue, #0042b8);
    font-size: 0.875rem;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    text-align: center;
  }
</style>
